import { useEffect, useMemo, useRef, useState } from "react";
import {
  Calculator, Volume2, VolumeX, ArrowRight, TrendingDown, Zap, Info,
} from "lucide-react";
import { usePricing } from "@/lib/PricingContext";
import { useI18n } from "@/lib/i18n";

// ---------- localized micro-copy ----------
const COPY = {
  de: {
    badge: "NEU · LIVE-PREIS",
    overline: "— Preis-in-30-Sekunden",
    title_1: "Ihr Richtpreis.",
    title_2: "In 30 Sekunden.",
    intro:
      "Verfahren wählen, Grösse angeben, Stückzahl setzen — der Preis-Rechner zeigt Ihnen sofort einen realistischen Richtpreis-Korridor. Ganz ohne Wartezeit, ganz ohne E-Mail.",
    step_verfahren: "Verfahren",
    step_size: "Bauteil-Grösse",
    step_qty: "Stückzahl",
    step_urgency: "Termin",
    live_label: "Richtpreis · gesamt",
    per_piece: "Pro Stück",
    range: "Bandbreite",
    savings_title: "So sparen Sie",
    no_discount: "Ab 100 Stk beginnt der Mengenrabatt.",
    volume_disc: (pct) => `${pct} % Mengenrabatt aktiv`,
    urgency_add: (pct) => `+${pct} % Termin-Aufschlag`,
    cta_offer: "Verbindliche Offerte anfordern",
    disclaimer:
      "Demo-Preise auf Basis Erfahrungswerten. Der verbindliche Preis wird nach Prüfung Ihrer Zeichnung erstellt. Rüstkosten, Materialaufwand und Sonderprozesse können abweichen.",
    sound_on: "Ton an",
    sound_off: "Ton aus",
    qty_hint: "z. B. 250",
    qty_unit: "Stk",
  },
  en: {
    badge: "NEW · LIVE PRICE",
    overline: "— Price-in-30-seconds",
    title_1: "Your ballpark price.",
    title_2: "In 30 seconds.",
    intro:
      "Pick a process, enter part size and quantity — the calculator instantly shows a realistic price corridor. No wait, no email required.",
    step_verfahren: "Process",
    step_size: "Part size",
    step_qty: "Quantity",
    step_urgency: "Timing",
    live_label: "Ballpark · total",
    per_piece: "Per piece",
    range: "Range",
    savings_title: "How you save",
    no_discount: "Volume discount starts at 100 pcs.",
    volume_disc: (pct) => `${pct}% volume discount applied`,
    urgency_add: (pct) => `+${pct}% urgency surcharge`,
    cta_offer: "Request a binding quote",
    disclaimer:
      "Demo pricing based on empirical data. The binding price is issued after we've reviewed your drawing. Set-up costs, material and special processes may vary.",
    sound_on: "Sound on",
    sound_off: "Sound off",
    qty_hint: "e.g. 250",
    qty_unit: "pcs",
  },
  fr: {
    badge: "NOUVEAU · PRIX EN DIRECT",
    overline: "— Prix-en-30-secondes",
    title_1: "Votre prix indicatif.",
    title_2: "En 30 secondes.",
    intro:
      "Choisissez un procédé, indiquez la taille et la quantité — le calculateur affiche instantanément une fourchette de prix réaliste. Sans attente, sans e-mail.",
    step_verfahren: "Procédé",
    step_size: "Taille de la pièce",
    step_qty: "Quantité",
    step_urgency: "Délai",
    live_label: "Prix indicatif · total",
    per_piece: "À la pièce",
    range: "Fourchette",
    savings_title: "Vos économies",
    no_discount: "Le rabais quantité démarre à 100 pièces.",
    volume_disc: (pct) => `${pct} % de rabais quantité appliqué`,
    urgency_add: (pct) => `+${pct} % supplément délai`,
    cta_offer: "Demander un devis ferme",
    disclaimer:
      "Tarifs de démonstration basés sur l'expérience. Le prix ferme est émis après contrôle de votre plan. Coûts de préparation, matière et procédés spéciaux peuvent varier.",
    sound_on: "Son actif",
    sound_off: "Son coupé",
    qty_hint: "ex. 250",
    qty_unit: "pcs",
  },
  it: {
    badge: "NUOVO · PREZZO LIVE",
    overline: "— Prezzo-in-30-secondi",
    title_1: "Il Suo prezzo indicativo.",
    title_2: "In 30 secondi.",
    intro:
      "Scelga il procedimento, indichi dimensioni e quantità — il calcolatore mostra subito una fascia di prezzo realistica. Senza attese, senza e-mail.",
    step_verfahren: "Procedimento",
    step_size: "Dimensione pezzo",
    step_qty: "Quantità",
    step_urgency: "Tempistica",
    live_label: "Prezzo indicativo · totale",
    per_piece: "Al pezzo",
    range: "Fascia",
    savings_title: "Come risparmiate",
    no_discount: "Lo sconto quantità parte da 100 pz.",
    volume_disc: (pct) => `${pct}% di sconto quantità attivo`,
    urgency_add: (pct) => `+${pct}% supplemento tempistica`,
    cta_offer: "Richiedi un'offerta vincolante",
    disclaimer:
      "Prezzi dimostrativi basati sull'esperienza. Il prezzo vincolante viene emesso dopo verifica del Suo disegno. Costi di attrezzaggio, materiale e processi speciali possono variare.",
    sound_on: "Audio on",
    sound_off: "Audio off",
    qty_hint: "es. 250",
    qty_unit: "pz",
  },
};

// ---------- localized label helpers ----------
function verfahrenLabel(PRICING, key, lang) {
  return PRICING[key]?.[`label_${lang}`] || PRICING[key]?.label_de || key;
}
function sizeLabel(SIZE_CLASSES, key, lang) {
  return SIZE_CLASSES[key]?.[`label_${lang}`] || SIZE_CLASSES[key]?.label_de || key;
}
function urgencyLabel(URGENCY, key, lang) {
  return URGENCY[key]?.[`label_${lang}`] || URGENCY[key]?.label_de || key;
}

// ---------- Web-Audio "tick" ---------- //
function playTick(ctx, volume = 0.06) {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(1650 + Math.random() * 150, ctx.currentTime);
  gain.gain.setValueAtTime(volume, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.035);
  osc.connect(gain).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.04);
}
function playChime(ctx) {
  if (!ctx) return;
  const now = ctx.currentTime;
  [880, 1320, 1760].forEach((f, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(f, now + i * 0.06);
    gain.gain.setValueAtTime(0.09, now + i * 0.06);
    gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.06 + 0.45);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + i * 0.06);
    osc.stop(now + i * 0.06 + 0.5);
  });
}

// ---------- Ticking number hook ---------- //
function useTicker(target, { soundOn, audioCtx, onFinish }) {
  const [display, setDisplay] = useState(target || 0);
  const rafRef = useRef(null);
  const fromRef = useRef(target || 0);
  const startRef = useRef(0);
  const lastTickRef = useRef(0);
  const finishedRef = useRef(false);

  useEffect(() => {
    if (target == null || Number.isNaN(target)) return;
    const from = display;
    fromRef.current = from;
    startRef.current = performance.now();
    finishedRef.current = false;
    const duration = 850;

    const step = (now) => {
      const t = Math.min(1, (now - startRef.current) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const val = from + (target - from) * eased;
      setDisplay(val);

      if (soundOn && now - lastTickRef.current > 55) {
        playTick(audioCtx);
        lastTickRef.current = now;
      }
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDisplay(target);
        if (!finishedRef.current) {
          finishedRef.current = true;
          if (soundOn) playChime(audioCtx);
          onFinish && onFinish();
        }
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target]);

  return display;
}

// ---------- Component ---------- //
export const PreisShow = () => {
  const { lang } = useI18n();
  const {
    PRICING, SIZE_CLASSES, QUANTITY_TIERS, URGENCY,
    computePrice, formatCHF,
  } = usePricing();
  const c = COPY[lang] || COPY.de;

  const [verfahren, setVerfahren] = useState("verzinken");
  const [size, setSize] = useState("small");
  const [quantity, setQuantity] = useState(250);
  const [urgency, setUrgency] = useState("standard");
  const [soundOn, setSoundOn] = useState(false);

  const audioCtxRef = useRef(null);
  const ensureAudio = () => {
    if (!audioCtxRef.current && typeof window !== "undefined") {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtxRef.current = new AC();
    }
    // Resume if suspended (autoplay policies)
    if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
  };

  const toggleSound = () => {
    ensureAudio();
    setSoundOn((s) => !s);
  };

  const result = useMemo(
    () => computePrice({ verfahren, size, quantity, urgency }),
    [verfahren, size, quantity, urgency]
  );

  const displayed = useTicker(result?.totalPrice ?? 0, {
    soundOn,
    audioCtx: audioCtxRef.current,
  });

  const scrollToOffer = () => {
    // Map lowercase pricing key -> canonical Verfahren name used in Contact form <select>.
    const verfahrenKeyToSelect = {
      verzinken: "Verzinken",
      trommelverzinken: "Trommelverzinken",
      "zink-nickel": "Zink-Nickel",
      "zink-mangan": "Zink-Mangan-Phosphatieren",
      "cns-beizen": "CNS-Beizen",
      "cns-elektropolieren": "CNS-Elektropolieren",
      "schleifen-buersten": "Schleifen · Bürsten",
      bruenieren: "Brünieren",
      vergolden: "Vergolden",
      "chemisch-nickel": "Chemisch-Nickel",
      "galvanisch-nickel": "Galvanisch-Nickel",
    };
    const message =
      `Preis-Rechner Angabe:\n` +
      `• Verfahren: ${PRICING[verfahren].label_de}\n` +
      `• Grösse: ${SIZE_CLASSES[size].label_de}\n` +
      `• Stückzahl: ${quantity}\n` +
      `• Termin: ${URGENCY[urgency].label_de}\n` +
      `• Richtpreis: CHF ${formatCHF(result?.totalPriceMin)} – ${formatCHF(result?.totalPriceMax)}\n\n` +
      `Bitte um verbindliche Offerte.`;
    try {
      window.dispatchEvent(new CustomEvent("fk:prefill-contact", {
        detail: {
          service_interest: verfahrenKeyToSelect[verfahren] || "",
          message,
        },
      }));
    } catch (_) { /* ignore */ }
    document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
  };

  // ---------- render ---------- //
  const savingsPct = (result?.discount || 0);
  const urgAdd = (result?.urgencyPct || 0);

  return (
    <section
      id="preis"
      data-testid="preis-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5 overflow-hidden"
    >
      {/* soft glow */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-white/[0.03] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-white/[0.02] blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 border border-white/15 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" />
                {c.badge}
              </span>
              <span>{c.overline}</span>
            </div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {c.title_1}
              <br />
              <span className="text-zinc-500">{c.title_2}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end justify-between gap-6">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
            <button
              type="button"
              onClick={toggleSound}
              data-testid="preis-sound-toggle"
              className="flex-none inline-flex items-center gap-2 border border-white/15 hover:border-white/40 px-3 py-2 text-[11px] uppercase tracking-[0.18em] text-zinc-300 hover:text-white transition-colors"
              aria-pressed={soundOn}
            >
              {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              {soundOn ? c.sound_on : c.sound_off}
            </button>
          </div>
        </div>

        {/* Main card */}
        <div className="reveal bg-[#121214] border border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* LEFT · Inputs */}
            <div className="lg:col-span-7 p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-white/10">
              {/* Verfahren */}
              <div className="mb-10">
                <label className="overline block mb-4">01 · {c.step_verfahren}</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                  {Object.keys(PRICING).map((key) => {
                    const active = verfahren === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => { setVerfahren(key); if (soundOn) { ensureAudio(); playTick(audioCtxRef.current, 0.09); } }}
                        data-testid={`preis-verfahren-${key}`}
                        className={`text-left p-3 md:p-4 transition-colors ${active ? "bg-white text-black" : "bg-[#121214] text-zinc-200 hover:bg-[#191A1D]"}`}
                      >
                        <div className="text-[11px] uppercase tracking-[0.14em] opacity-60 mb-1">
                          CHF {PRICING[key].base_chf_per_dm2.toFixed(1)}/dm²
                        </div>
                        <div className="text-sm md:text-[15px] font-display font-medium tracking-tight leading-tight">
                          {verfahrenLabel(PRICING, key, lang)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Grösse */}
              <div className="mb-10">
                <label className="overline block mb-4">02 · {c.step_size}</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
                  {Object.keys(SIZE_CLASSES).map((key) => {
                    const active = size === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => { setSize(key); if (soundOn) { ensureAudio(); playTick(audioCtxRef.current, 0.09); } }}
                        data-testid={`preis-size-${key}`}
                        className={`p-3 md:p-4 text-left transition-colors ${active ? "bg-white text-black" : "bg-[#121214] text-zinc-200 hover:bg-[#191A1D]"}`}
                      >
                        <div className="text-[11px] uppercase tracking-[0.14em] opacity-60 mb-1">
                          {SIZE_CLASSES[key].dm2} dm²
                        </div>
                        <div className="text-[13px] font-display font-medium tracking-tight leading-tight">
                          {sizeLabel(SIZE_CLASSES, key, lang)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Stückzahl + Termin */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="overline block mb-4">03 · {c.step_qty}</label>
                  <div className="flex items-baseline gap-3">
                    <input
                      type="number"
                      min={1}
                      max={100000}
                      step={1}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                      onBlur={() => { if (soundOn) { ensureAudio(); playTick(audioCtxRef.current, 0.09); } }}
                      data-testid="preis-qty"
                      placeholder={c.qty_hint}
                      className="field font-display text-4xl md:text-5xl font-semibold !py-2"
                    />
                    <span className="text-zinc-500 text-sm">{c.qty_unit}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {[50, 100, 500, 2000, 10000].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => { setQuantity(preset); if (soundOn) { ensureAudio(); playTick(audioCtxRef.current, 0.09); } }}
                        data-testid={`preis-qty-preset-${preset}`}
                        className={`px-2.5 py-1 text-[11px] border transition-colors ${quantity === preset ? "border-white bg-white text-black" : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"}`}
                      >
                        {preset.toLocaleString("de-CH").replace(/,/g, "'")}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="overline block mb-4">04 · {c.step_urgency}</label>
                  <div className="space-y-1.5">
                    {Object.keys(URGENCY).map((key) => {
                      const active = urgency === key;
                      const surcharge = URGENCY[key].pct;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => { setUrgency(key); if (soundOn) { ensureAudio(); playTick(audioCtxRef.current, 0.09); } }}
                          data-testid={`preis-urgency-${key}`}
                          className={`w-full flex items-center justify-between px-3 py-2.5 border text-left transition-colors ${active ? "bg-white text-black border-white" : "bg-transparent text-zinc-300 border-white/15 hover:border-white/40 hover:text-white"}`}
                        >
                          <span className="text-[13px] font-medium">{urgencyLabel(URGENCY, key, lang)}</span>
                          <span className={`text-[11px] ${active ? "text-black/60" : "text-zinc-500"}`}>
                            {surcharge === 0 ? "±0 %" : `+${surcharge} %`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT · Live Price */}
            <div className="lg:col-span-5 p-8 md:p-12 bg-gradient-to-br from-[#0E0E10] to-[#141416] flex flex-col">
              <div className="overline mb-4 flex items-center gap-2">
                <Calculator className="w-3.5 h-3.5" />
                {c.live_label}
              </div>

              {/* BIG NUMBER */}
              <div
                data-testid="preis-total"
                className="font-display font-semibold text-white tracking-tight leading-none tabular-nums text-5xl md:text-6xl lg:text-7xl mb-2"
              >
                CHF {formatCHF(displayed)}
              </div>
              <div className="text-zinc-400 text-sm mb-8">
                {c.range}: CHF {formatCHF(result?.totalPriceMin)} – {formatCHF(result?.totalPriceMax)}
              </div>

              {/* per piece + selected process */}
              <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mb-6">
                <div className="bg-[#0E0E10] p-4">
                  <div className="overline mb-1.5">{c.per_piece}</div>
                  <div
                    data-testid="preis-per-piece"
                    className="font-display text-xl text-white tabular-nums"
                  >
                    CHF {result ? result.unitPrice.toFixed(2).replace(".", ".") : "—"}
                  </div>
                </div>
                <div className="bg-[#0E0E10] p-4">
                  <div className="overline mb-1.5">{c.step_verfahren}</div>
                  <div className="text-sm text-white font-medium leading-tight">
                    {verfahrenLabel(PRICING, verfahren, lang)}
                  </div>
                </div>
              </div>

              {/* Savings bar */}
              <div className="border border-white/10 p-5 mb-6">
                <div className="overline mb-4 flex items-center gap-2">
                  <TrendingDown className="w-3.5 h-3.5" />
                  {c.savings_title}
                </div>
                {/* Discount progress bar */}
                <div className="relative h-1.5 bg-white/10 mb-3">
                  <div
                    className="absolute inset-y-0 left-0 bg-emerald-400/80 transition-all duration-500"
                    style={{ width: `${Math.min(100, (savingsPct / 20) * 100)}%` }}
                    data-testid="preis-discount-bar"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {QUANTITY_TIERS.map((tier) => {
                    const reached = quantity >= tier.min;
                    return (
                      <span
                        key={tier.min}
                        className={`inline-flex items-center px-2 py-1 text-[11px] border transition-colors ${reached ? "border-emerald-400/60 text-emerald-300 bg-emerald-400/5" : "border-white/10 text-zinc-500"}`}
                      >
                        {tier.min.toLocaleString("de-CH").replace(/,/g, "'")}+
                      </span>
                    );
                  })}
                </div>
                <div className="text-[12px] text-zinc-400 leading-relaxed">
                  {savingsPct > 0
                    ? <><span className="text-emerald-300 font-semibold">–{savingsPct} %</span>{" "}{c.volume_disc_short || c.volume_disc(savingsPct).replace(new RegExp(`^${savingsPct}\\s?%\\s?`), "")}</>
                    : c.no_discount}
                  {urgAdd > 0 && (
                    <span className="inline-flex items-center gap-1 ml-2 text-amber-300">
                      <Zap className="w-3 h-3" /> {c.urgency_add(urgAdd)}
                    </span>
                  )}
                </div>
              </div>

              {/* CTA */}
              <button
                type="button"
                onClick={scrollToOffer}
                data-testid="preis-cta-offer"
                className="group inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-4 text-sm font-semibold tracking-tight hover:bg-zinc-200 transition-colors duration-300 shine"
              >
                {c.cta_offer}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>

              {/* Disclaimer */}
              <div className="mt-6 flex gap-2 text-[11px] text-zinc-500 leading-relaxed">
                <Info className="w-3.5 h-3.5 flex-none mt-0.5" />
                <span>{c.disclaimer}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PreisShow;
